"use client";

import { Button } from "@/components/rac/button";
import { useIsMobile } from "@/hooks/use-mobile";
import { Z_INDEX } from "@/lib/constants/z-index";
import { cn } from "@/utils/cn";
import { addOverlayOpenAttribute, removeOverlayOpenAttribute } from "@/utils/overlay-utils";
import { X } from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import { Dialog, Modal, ModalOverlay, type ModalOverlayProps } from "react-aria-components";
import { DragHandle } from "./drag-handle";

export interface OverlayProps extends Omit<ModalOverlayProps, "children" | "isOpen"> {
	/** Whether the overlay is open */
	isOpen: boolean;
	/** Callback fired when the overlay should close */
	onClose?: () => void;
	/** Content to display in the overlay */
	children: ReactNode;
	/** Additional CSS class name for the modal */
	className?: string;
	/** Whether the overlay can be dismissed by clicking outside or pressing escape */
	isDismissable?: boolean;
}

export function Overlay({ isOpen, onClose, children, className, isDismissable = true, ...props }: OverlayProps) {
	// Use media query hook for mobile detection to avoid layout reads on resize
	const isMobile = useIsMobile();
	const [dragY, setDragY] = useState(0);

	// Toggle attribute when mobile overlay is open
	useEffect(() => {
		if (isOpen && isMobile) {
			addOverlayOpenAttribute();
		} else {
			removeOverlayOpenAttribute();
		}

		return () => {
			removeOverlayOpenAttribute();
		};
	}, [isOpen, isMobile]);

	if (isMobile) {
		return (
			<ModalOverlay
				isOpen={isOpen}
				onOpenChange={open => !open && onClose?.()}
				isDismissable={isDismissable}
				className={cn("fixed inset-0 flex items-end justify-center bg-black/50", Z_INDEX.OVERLAY)}
				{...props}
			>
				<div
					className={cn(
						"relative w-full bg-white rounded-t-2xl shadow-xl z-10 max-h-[90vh]",
						"data-[entering]:animate-in data-[entering]:slide-in-from-bottom data-[entering]:duration-300 data-[entering]:ease-out",
						"data-[exiting]:animate-out data-[exiting]:slide-out-to-bottom data-[exiting]:duration-200 data-[exiting]:ease-in",
						className,
					)}
					style={{
						transform: dragY > 0 ? `translateY(${dragY}px)` : undefined,
						transition: dragY > 0 ? "none" : undefined,
					}}
				>
					<Button
						variant="bareshell"
						className="absolute -top-14 right-4 p-2 rounded-full bg-gray-100 hover:bg-gray-200 shadow-lg transition-colors z-50"
						onPress={() => onClose?.()}
						aria-label="Close dialog"
					>
						<X className="h-6 w-6 text-gray-800" />
					</Button>
					<Modal>
						<Dialog className="outline-none unfocusable h-full flex flex-col" aria-label="Overlay">
							{/* Drag handle - swipe-to-dismiss ONLY works on this handle */}
							<DragHandle onDrag={setDragY} onClose={() => onClose?.()} />

							{/* Scrollable content area */}
							<div className="flex-1 overflow-y-auto p-2">{children}</div>
						</Dialog>
					</Modal>
				</div>
			</ModalOverlay>
		);
	}

	return (
		<ModalOverlay
			isOpen={isOpen}
			onOpenChange={open => !open && onClose?.()}
			isDismissable={isDismissable}
			className={cn("fixed inset-0 flex bg-black/50", Z_INDEX.OVERLAY)}
			{...props}
		>
			<div
				className={cn(
					"relative w-[500px] max-w-[90dvw] h-full overflow-y-auto bg-white shadow-xl z-10",
					"data-[entering]:animate-in data-[entering]:slide-in-from-left data-[entering]:duration-300 data-[entering]:ease-out",
					"data-[exiting]:animate-out data-[exiting]:slide-out-to-left data-[exiting]:duration-200 data-[exiting]:ease-in",
					className,
				)}
			>
				<Modal>
					<Dialog className="h-full outline-none">{children}</Dialog>
				</Modal>
			</div>
		</ModalOverlay>
	);
}
